import type { MoreFieldMappingItem } from 'src/interfaces/Template.interface'
import { fieldIdFromMoreMappingTemplate } from './quickListMoreMappingIdentity'

/**
 * Saved miscellaneous templates for one listing-profile edit.
 * Kept apart from the channel field catalog so a catalog response that arrives
 * later can still be filled, and a catalog response that arrives earlier is not
 * the only chance to attach saved values.
 */
export type MiscHydratedTemplate = {
  fieldId: number | null
  templateId: number
  moreMappingData: Record<string, any>
}

export type MiscTemplateRef = {
  fieldId: number | null
  templateId: number
}

export type MiscSelectOption = {
  value: number
  label: string
}

export type MiscHydrateStore = {
  recipeId: number
  platformId: number | null
  templates: MiscHydratedTemplate[]
  /** Template ids that still failed after one retry. Omitted means "leave the previous list". */
  failedRefs?: MiscTemplateRef[]
}

export type MiscMappingOption = 'DEFAULT' | 'NATIVE' | 'CUSTOM'

/** Mapping type to show on first paint, before per-field effects run. */
export const mappingOptionFromSavedField = (mappedField: unknown): MiscMappingOption => {
  if (!mappedField || typeof mappedField !== 'object') return 'DEFAULT'
  const fieldType = (mappedField as { field_type?: string }).field_type
  if (fieldType === 'LOCAL' || fieldType === 'NATIVE') return 'NATIVE'
  if (fieldType === 'CUSTOM') return 'CUSTOM'
  return 'DEFAULT'
}

export const mergeMiscTemplates = (
  current: MiscHydratedTemplate[],
  incoming: MiscHydratedTemplate[]
): MiscHydratedTemplate[] => {
  const byId = new Map(current.map((template) => [template.templateId, template]))
  incoming.forEach((template) => byId.set(template.templateId, template))
  return [...byId.values()]
}

/** GET /platform-field-template/:id is sometimes the row and sometimes wrapped. */
export const unwrapPlatformFieldTemplate = (payload: unknown): Record<string, any> | null => {
  if (!payload || typeof payload !== 'object') return null
  const row = payload as Record<string, any>
  if (isTemplateRow(row)) return row
  if (row.data && typeof row.data === 'object' && isTemplateRow(row.data)) {
    return row.data as Record<string, any>
  }
  if (Array.isArray(row.results)) {
    const first = row.results.find((item) => isTemplateRow(item))
    return (first as Record<string, any>) ?? null
  }
  return null
}

const isTemplateRow = (value: unknown): boolean => {
  if (!value || typeof value !== 'object') return false
  const id = (value as { id?: unknown }).id
  return id != null && id !== '' && Number.isFinite(Number(id)) && Number(id) > 0
}

export const normalizeFetchedMiscTemplate = (
  ref: { fieldId: number | null; templateId: number },
  payload: unknown
): MiscHydratedTemplate | null => {
  const found = unwrapPlatformFieldTemplate(payload)
  if (!found) return null
  return {
    fieldId: ref.fieldId ?? fieldIdFromMoreMappingTemplate(found),
    templateId: ref.templateId,
    moreMappingData: found,
  }
}

export const templateMatchesField = (
  fieldId: number,
  template: MiscHydratedTemplate
): boolean => {
  const data = template.moreMappingData
  if (template.fieldId != null && Number(template.fieldId) === Number(fieldId)) return true
  const platformField = data?.platform_field
  if (platformField && typeof platformField === 'object' && platformField.id != null) {
    return Number(platformField.id) === Number(fieldId)
  }
  if (platformField != null && typeof platformField !== 'object') {
    return Number(platformField) === Number(fieldId)
  }
  return false
}

/** Drop saved rows that arrived on a previous edit. The catalog is only the field list. */
export const stripMiscSavedTemplates = (
  fields: MoreFieldMappingItem[]
): MoreFieldMappingItem[] =>
  fields.map((field) => {
    if (!field || !('moreMappingData' in field)) return field
    const { moreMappingData: _saved, ...rest } = field
    return rest as MoreFieldMappingItem
  })

const NO_TEMPLATES: MiscHydratedTemplate[] = []

export const selectMiscTemplatesForEdit = (
  store: MiscHydrateStore | null,
  editingRecipeId: number | null,
  platformId: number | null | undefined
): MiscHydratedTemplate[] => {
  if (!store || editingRecipeId == null) return NO_TEMPLATES
  if (Number(store.recipeId) !== Number(editingRecipeId)) return NO_TEMPLATES
  if (
    store.platformId != null &&
    platformId != null &&
    Number(store.platformId) !== Number(platformId)
  ) {
    return NO_TEMPLATES
  }
  return store.templates
}

export const applyMiscTemplatesToCatalog = (
  catalog: MoreFieldMappingItem[],
  templates: MiscHydratedTemplate[]
): MoreFieldMappingItem[] =>
  catalog.map((item) => {
    const match = templates.find((template) => templateMatchesField(item.id, template))
    if (!match?.moreMappingData) return item
    return { ...item, moreMappingData: match.moreMappingData }
  })

/**
 * Overlay (AI Auto-Map, or any full-list write) wins for fields it already
 * decided. Saved templates fill only fields the overlay does not mention.
 * Re-running this with the same template objects keeps Formik initial values stable.
 */
export const deriveMiscFieldRows = ({
  catalog,
  templates,
  overlay,
}: {
  catalog: MoreFieldMappingItem[]
  templates: MiscHydratedTemplate[]
  overlay: MoreFieldMappingItem[] | null
}): MoreFieldMappingItem[] => {
  if (!overlay) return applyMiscTemplatesToCatalog(catalog, templates)

  return catalog.map((item) => {
    const fromOverlay = overlay.find((row) => Number(row.id) === Number(item.id))
    if (fromOverlay) {
      return fromOverlay.moreMappingData
        ? { ...item, moreMappingData: fromOverlay.moreMappingData }
        : { ...item, moreMappingData: null }
    }
    const match = templates.find((template) => templateMatchesField(item.id, template))
    if (!match?.moreMappingData) return item
    return { ...item, moreMappingData: match.moreMappingData }
  })
}

export const toMiscNumericId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const num = Number(value)
  return Number.isFinite(num) ? num : null
}

export const sameMiscOptionId = (left: unknown, right: unknown): boolean => {
  const a = toMiscNumericId(left)
  const b = toMiscNumericId(right)
  return a !== null && a === b
}

/** Label carried on a saved mapped_field or value object. */
export const optionFromSavedMappingRef = (ref: unknown): MiscSelectOption | null => {
  if (!ref || typeof ref !== 'object') return null
  const row = ref as { id?: unknown; display_name?: string; api_name?: string }
  const id = toMiscNumericId(row.id)
  if (id == null) return null
  const label = (row.display_name || row.api_name || '').trim()
  return { value: id, label: label || String(id) }
}

/**
 * Show the saved choice when the loaded page does not contain it.
 * A page that already has the id is left as-is, so the option is not listed twice.
 * A cleared control (no selected id) does not get the saved option put back.
 */
export const withSavedSelectOption = (
  options: MiscSelectOption[],
  saved: MiscSelectOption | null,
  selectedId: unknown
): MiscSelectOption[] => {
  if (!saved) return options
  if (options.some((opt) => sameMiscOptionId(opt.value, saved.value))) return options
  if (!sameMiscOptionId(selectedId, saved.value)) return options
  return [saved, ...options]
}

const loadOneMiscTemplate = async (
  ref: MiscTemplateRef,
  fetchPayload: (templateId: number) => Promise<unknown>
): Promise<MiscHydratedTemplate | null> => {
  try {
    const payload = await fetchPayload(ref.templateId)
    return normalizeFetchedMiscTemplate(ref, payload)
  } catch {
    return null
  }
}

/**
 * Load each saved miscellaneous template, then retry the failures once.
 * A template that still fails is returned in `failedRefs` instead of being dropped.
 */
export const fetchMiscTemplatesWithRetry = async (
  refs: MiscTemplateRef[],
  fetchPayload: (templateId: number) => Promise<unknown>
): Promise<{ templates: MiscHydratedTemplate[]; failedRefs: MiscTemplateRef[] }> => {
  const first = await Promise.all(
    refs.map(async (ref) => ({ ref, row: await loadOneMiscTemplate(ref, fetchPayload) }))
  )
  const missed = first.filter((item) => !item.row)
  const retried = await Promise.all(
    missed.map(async (item) => ({
      ref: item.ref,
      row: await loadOneMiscTemplate(item.ref, fetchPayload),
    }))
  )

  return {
    templates: [
      ...first.flatMap((item) => (item.row ? [item.row] : [])),
      ...retried.flatMap((item) => (item.row ? [item.row] : [])),
    ],
    failedRefs: retried.filter((item) => !item.row).map((item) => item.ref),
  }
}

/**
 * Miscellaneous native/custom lists are not ready to paint while the source
 * store is still unknown, or while its field lists are still loading.
 * A finished lookup that found no source store does not block the section.
 */
export const areMiscSourceFieldsReady = ({
  viewMode,
  editingRecipeId,
  sourceStoreId,
  sourceFieldListsLoadedFor,
  sourcePlatformResolved,
  shouldLoadFormPlatformData,
}: {
  viewMode: 'list' | 'create' | 'edit'
  editingRecipeId: number | null
  sourceStoreId: number | null | undefined
  sourceFieldListsLoadedFor: number | null
  sourcePlatformResolved: boolean
  shouldLoadFormPlatformData: boolean
}): boolean => {
  if (viewMode !== 'edit' || editingRecipeId == null) return true
  const listsPending =
    sourceStoreId != null && sourceFieldListsLoadedFor !== sourceStoreId
  const storePending =
    sourceStoreId == null && shouldLoadFormPlatformData && !sourcePlatformResolved
  return !listsPending && !storePending
}
